<template>
  <div class="po-dash-page cobros-page">
    <NavbarAdmin v-if="esAdmin()" />
    <NavbarMain v-else />
    <div class="container po-dash-container py-4 pb-5">
      <header class="po-dash-head d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <p class="po-dash-head__eyebrow text-uppercase mb-1">Ventas</p>
          <h1 class="po-dash-head__title h4 mb-0">Ventas POS</h1>
          <p class="po-dash-head__lead text-muted small mb-0">
            Cobros del mostrador: detalle, facturación ARCA y reembolsos.
            Los pedidos online están en
            <router-link to="/u/pedidos">Pedidos</router-link>.
          </p>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-success btn-sm rounded-pill"
            :disabled="cargando || !cobrosFiltrados.length"
            @click="exportar"
          >
            <i class="bi bi-file-earmark-excel me-1" aria-hidden="true"></i>Exportar
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm rounded-pill" :disabled="cargando" @click="cargar">
            <i class="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>Actualizar
          </button>
        </div>
      </header>

      <div v-if="avisoTabla" class="alert alert-warning">{{ avisoTabla }}</div>
      <div v-if="!appOnline" class="alert alert-secondary small mb-3">
        Modo offline: se muestran ventas de este dispositivo. Para reembolsar o facturar necesitás conexión.
      </div>

      <div class="row g-2 g-md-3 mb-3">
        <div class="col-6 col-lg-3">
          <div class="cobro-stat">
            <span class="cobro-stat__label">Ventas hoy</span>
            <span class="cobro-stat__num">{{ resumenHoy.cantidad }}</span>
          </div>
        </div>
        <div class="col-6 col-lg-3">
          <div class="cobro-stat cobro-stat--accent">
            <span class="cobro-stat__label">Total hoy</span>
            <span class="cobro-stat__num">${{ money(resumenHoy.total) }}</span>
          </div>
        </div>
        <div class="col-6 col-lg-3">
          <div class="cobro-stat">
            <span class="cobro-stat__label">Efectivo hoy</span>
            <span class="cobro-stat__num">${{ money(resumenHoy.efectivo) }}</span>
          </div>
        </div>
        <div class="col-6 col-lg-3">
          <div class="cobro-stat">
            <span class="cobro-stat__label">Reembolsos hoy</span>
            <span class="cobro-stat__num">${{ money(resumenHoy.reembolsos) }}</span>
          </div>
        </div>
      </div>

      <div class="cobros-toolbar mb-3">
        <div class="btn-group btn-group-sm" role="group" aria-label="Período">
          <button type="button" class="btn" :class="rango === 'hoy' ? 'btn-dark' : 'btn-outline-secondary'" @click="rango = 'hoy'">Hoy</button>
          <button type="button" class="btn" :class="rango === 'todos' ? 'btn-dark' : 'btn-outline-secondary'" @click="rango = 'todos'">Todos</button>
        </div>
        <div class="btn-group btn-group-sm" role="group" aria-label="Estado">
          <button type="button" class="btn" :class="filtroEstado === 'activas' ? 'btn-dark' : 'btn-outline-secondary'" @click="filtroEstado = 'activas'">Cobradas</button>
          <button type="button" class="btn" :class="filtroEstado === 'canceladas' ? 'btn-dark' : 'btn-outline-secondary'" @click="filtroEstado = 'canceladas'">Reembolsadas</button>
        </div>
        <div v-if="puedeFacturarArca" class="btn-group btn-group-sm" role="group" aria-label="Facturación ARCA">
          <button type="button" class="btn" :class="filtroArca === 'todas' ? 'btn-dark' : 'btn-outline-secondary'" @click="filtroArca = 'todas'">Todas</button>
          <button type="button" class="btn" :class="filtroArca === 'facturadas' ? 'btn-dark' : 'btn-outline-secondary'" @click="filtroArca = 'facturadas'">Facturadas</button>
          <button type="button" class="btn" :class="filtroArca === 'pendientes' ? 'btn-dark' : 'btn-outline-secondary'" @click="filtroArca = 'pendientes'">Sin factura</button>
        </div>
        <div class="cobros-toolbar__search">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input v-model="busqueda" type="search" class="form-control form-control-sm" placeholder="Cliente, vendedor o medio…" />
        </div>
      </div>

      <div v-if="cargando" class="text-center text-muted py-5">Cargando…</div>

      <div v-else-if="!cobrosFiltrados.length" class="card border-0 shadow-sm">
        <div class="card-body text-center text-muted py-5">
          No hay ventas para mostrar. Cobrás desde
          <router-link to="/u/pos">POS</router-link>.
        </div>
      </div>

      <div v-else class="vstack gap-2">
        <article
          v-for="p in cobrosFiltrados"
          :key="p.pedido_id"
          :id="'pedido-' + p.pedido_id"
          class="card border-0 shadow-sm cobro-card"
          :class="{
            'cobro-card--open': expandido === p.pedido_id,
            'cobro-card--cancel': esCancelado(p),
          }"
        >
          <button
            type="button"
            class="cobro-card__hit w-100 text-start btn btn-link text-decoration-none text-body p-0"
            :aria-expanded="expandido === p.pedido_id ? 'true' : 'false'"
            @click="toggleExpand(p)"
          >
            <div class="cobro-card__top">
              <div class="cobro-card__chips">
                <span class="cobro-chip cobro-chip--pay">{{ p.medio_pago || 'Cobro' }}</span>
                <span v-if="esCancelado(p)" class="cobro-chip cobro-chip--cancel">Reembolsada</span>
                <span v-else-if="estadoArca(p) === 'nc'" class="cobro-chip cobro-chip--nc">Nota de crédito</span>
                <span v-else-if="estadoArca(p) === 'factura'" class="cobro-chip cobro-chip--ok">Facturada</span>
                <span v-else-if="puedeFacturarArca && !p.offline" class="cobro-chip cobro-chip--wait">Sin factura</span>
                <span v-if="p.offline" class="cobro-chip cobro-chip--off">{{ etiquetaSync(p) }}</span>
              </div>
              <div class="cobro-card__amount" :class="{ 'text-decoration-line-through text-muted': esCancelado(p) }">
                ${{ money(p.total) }}
              </div>
            </div>
            <div class="cobro-card__meta">
              <div class="min-w-0">
                <div class="fw-semibold text-truncate">{{ p.cliente_nombre || 'Consumidor final' }}</div>
                <div class="small text-muted text-truncate">
                  {{ formatearFecha(p.creado_en) }}
                  <span v-if="p.vendedor"> · {{ p.vendedor }}</span>
                  <span> · {{ itemsDe(p).length }} ítem(s)</span>
                </div>
              </div>
              <i
                class="bi bi-chevron-down cobro-card__chev"
                :class="{ 'cobro-card__chev--up': expandido === p.pedido_id }"
                aria-hidden="true"
              />
            </div>
          </button>

          <div v-if="expandido === p.pedido_id" class="cobro-card__detail">
            <section>
              <h2 class="cobro-card__h">Productos</h2>
              <ul class="list-unstyled mb-0 cobro-items">
                <li v-for="(it, idx) in itemsDe(p)" :key="idx" class="cobro-items__row">
                  <div class="min-w-0">
                    <div class="fw-medium">{{ it.producto_nombre }}</div>
                    <div class="small text-muted">
                      {{ it.cantidadSeleccionada }} × ${{ money(precioUnitario(it)) }}
                    </div>
                  </div>
                  <span class="text-nowrap">${{ money(subtotalItem(it)) }}</span>
                </li>
              </ul>
            </section>

            <section v-if="puedeFacturarArca" class="cobro-card__arca">
              <h2 class="cobro-card__h">Facturación ARCA</h2>
              <p v-if="facturaDe(p)" class="small mb-2">
                <i class="bi bi-check-circle-fill text-success me-1" aria-hidden="true" />
                {{ facturaDe(p).cbte_tipo_label }}
                {{ facturaDe(p).numero_completo }}
                <span v-if="facturaDe(p).cae" class="text-muted"> · CAE {{ facturaDe(p).cae }}</span>
              </p>
              <p v-if="ncDe(p)" class="small mb-2">
                <i class="bi bi-arrow-counterclockwise text-primary me-1" aria-hidden="true" />
                {{ ncDe(p).cbte_tipo_label }}
                {{ ncDe(p).numero_completo }}
              </p>
              <p v-if="!facturaDe(p) && !esCancelado(p)" class="small text-muted mb-2">
                Esta venta todavía no tiene comprobante electrónico.
              </p>
            </section>

            <div class="cobro-card__actions">
              <button
                v-if="puedeFacturarArca && !facturaDe(p) && !esCancelado(p) && !p.offline"
                type="button"
                class="btn btn-primary btn-sm rounded-pill"
                @click.stop="abrirFacturaArca(p)"
              >
                <i class="bi bi-receipt me-1" aria-hidden="true" />Facturar ARCA
              </button>
              <button
                v-if="puedeFacturarArca && facturaDe(p) && !ncDe(p) && !p.offline"
                type="button"
                class="btn btn-outline-primary btn-sm rounded-pill"
                @click.stop="abrirNotaCredito(p)"
              >
                <i class="bi bi-arrow-counterclockwise me-1" aria-hidden="true" />Nota de crédito
              </button>
              <button
                v-if="puedeReembolsar(p)"
                type="button"
                class="btn btn-outline-danger btn-sm rounded-pill"
                :disabled="reembolsando === p.pedido_id || !appOnline"
                @click.stop="reembolsar(p)"
              >
                <span v-if="reembolsando === p.pedido_id" class="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                <i v-else class="bi bi-cash-stack me-1" aria-hidden="true" />
                Reembolsar
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>

    <EmitirComprobanteArcaModal
      v-model="arcaModalAbierto"
      :preset="arcaPreset"
      @emitido="onArcaEmitido"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import NavbarAdmin from '@/components/layout/NavbarAdmin.vue';
import NavbarMain from '@/components/layout/NavbarMain.vue';
import { isAdmin } from '@/utils/session';
import { hasFeature } from '@/utils/features';
import { esVentaPos } from '@/utils/pedidoOrigen';
import { connectivity, onConnectivityChange } from '@/utils/connectivity';
import { resolveCobrosList } from '@/utils/posOffline/cache';
import { subscribePosSyncEvents } from '@/utils/posOffline/sync';
import EmitirComprobanteArcaModal from '@/components/arca/EmitirComprobanteArcaModal.vue';
import axios from 'axios';
import Swal from 'sweetalert2';

const TIPOS_NC = [3, 8, 13];
const TIPOS_FACTURA = [1, 6, 11];

export default {
  name: 'CobrosView',
  components: { NavbarAdmin, NavbarMain, EmitirComprobanteArcaModal },
  data() {
    return {
      cobros: [],
      cargando: true,
      avisoTabla: '',
      expandido: null,
      rango: 'hoy',
      busqueda: '',
      filtroEstado: 'activas',
      filtroArca: 'todas',
      offConnectivity: null,
      offSyncEvents: null,
      arcaVinculado: false,
      arcaModalAbierto: false,
      arcaPreset: null,
      comprobantesArca: [],
      reembolsando: null,
    };
  },
  computed: {
    puedeFacturarArca() {
      return hasFeature('integracion_arca') && this.arcaVinculado;
    },
    appOnline() {
      return connectivity.online;
    },
    cobrosMostrador() {
      return this.cobros.filter((p) => p.offline || this.esVentaPos(p));
    },
    cobrosFiltrados() {
      const term = this.busqueda.trim().toLowerCase();
      return this.cobrosMostrador.filter((p) => {
        if (this.rango === 'hoy' && !this.esHoy(p.creado_en)) return false;
        const cancel = this.esCancelado(p);
        if (this.filtroEstado === 'activas' && cancel) return false;
        if (this.filtroEstado === 'canceladas' && !cancel) return false;
        if (this.puedeFacturarArca && this.filtroArca !== 'todas') {
          const st = this.estadoArca(p);
          if (this.filtroArca === 'facturadas' && st !== 'factura' && st !== 'nc') return false;
          if (this.filtroArca === 'pendientes' && (st === 'factura' || st === 'nc' || cancel)) return false;
        }
        if (!term) return true;
        const hay = `${p.vendedor || ''} ${p.cliente_nombre || ''} ${p.medio_pago || ''}`.toLowerCase();
        return hay.includes(term);
      });
    },
    resumenHoy() {
      const hoy = this.cobrosMostrador.filter((p) => this.esHoy(p.creado_en));
      let total = 0;
      let efectivo = 0;
      let reembolsos = 0;
      let cantidad = 0;
      for (const p of hoy) {
        const t = Number(p.total) || 0;
        if (this.esCancelado(p)) {
          reembolsos += t;
          continue;
        }
        cantidad += 1;
        total += t;
        if (String(p.medio_pago || '').toLowerCase().includes('efectivo')) efectivo += t;
      }
      return { cantidad, total, efectivo, reembolsos };
    },
  },
  watch: {
    '$route.query.pedido'() {
      this.aplicarPedidoDesdeQuery();
    },
  },
  beforeUnmount() {
    if (this.offConnectivity) this.offConnectivity();
    if (this.offSyncEvents) this.offSyncEvents();
  },
  mounted() {
    const u = localStorage.getItem('usuario');
    if (!u) {
      this.$router.push('/u/login');
      return;
    }
    this.setupLiveUpdates();
    this.cargar();
    if (hasFeature('integracion_arca')) this.cargarEstadoArca();
  },
  methods: {
    async cargarEstadoArca() {
      try {
        const { data } = await axios.get('/arca/estado');
        this.arcaVinculado = !!data.vinculado;
        if (this.arcaVinculado) await this.cargarComprobantesArca();
      } catch {
        this.arcaVinculado = false;
      }
    },
    async cargarComprobantesArca() {
      try {
        const { data } = await axios.get('/arca/comprobantes', { params: { limit: 300 } });
        this.comprobantesArca = Array.isArray(data.items) ? data.items : [];
      } catch {
        this.comprobantesArca = [];
      }
    },
    cbsDe(p) {
      const id = Number(p && p.pedido_id);
      if (!id) return [];
      return this.comprobantesArca.filter((c) => Number(c.pedido_id) === id && c.ok);
    },
    facturaDe(p) {
      return (
        this.cbsDe(p).find(
          (c) => c.clase === 'factura' || TIPOS_FACTURA.includes(Number(c.cbte_tipo))
        ) || null
      );
    },
    ncDe(p) {
      return this.cbsDe(p).find((c) => c.clase === 'nc' || TIPOS_NC.includes(Number(c.cbte_tipo))) || null;
    },
    estadoArca(p) {
      if (this.ncDe(p)) return 'nc';
      if (this.facturaDe(p)) return 'factura';
      return 'ninguna';
    },
    esCancelado(p) {
      return String((p && p.estado) || '') === 'cancelado';
    },
    puedeReembolsar(p) {
      return !this.esCancelado(p) && !p.offline && this.esVentaPos(p);
    },
    precioUnitario(it) {
      const q = Number(it.cantidadSeleccionada) || 1;
      if (it.producto_precio != null) return Number(it.producto_precio) || 0;
      return q ? (Number(it.subtotal) || 0) / q : 0;
    },
    subtotalItem(it) {
      const q = Number(it.cantidadSeleccionada) || 0;
      return q * this.precioUnitario(it);
    },
    abrirFacturaArca(p) {
      this.arcaPreset = {
        pedido_id: p.pedido_id,
        imp_total: Number(p.total) || null,
        receptor_nombre: p.cliente_nombre || '',
      };
      this.arcaModalAbierto = true;
    },
    abrirNotaCredito(p) {
      const fac = this.facturaDe(p);
      if (!fac) return;
      const ncTipo = fac.cbte_tipo === 1 ? 3 : fac.cbte_tipo === 6 ? 8 : fac.cbte_tipo === 11 ? 13 : null;
      this.arcaPreset = {
        pedido_id: p.pedido_id,
        imp_total: Number(fac.imp_total != null ? fac.imp_total : p.total) || null,
        receptor_nombre: fac.receptor_nombre || p.cliente_nombre || '',
        cbte_tipo: ncTipo,
        punto_venta: fac.punto_venta,
        doc_tipo: fac.doc_tipo,
        doc_nro: fac.doc_nro,
        condicion_iva_receptor: fac.condicion_iva_receptor,
        iva_alicuota: fac.iva_alicuota,
        asociado_tipo: fac.cbte_tipo,
        asociado_pto_vta: fac.punto_venta,
        asociado_nro: fac.cbte_nro,
      };
      this.arcaModalAbierto = true;
    },
    async onArcaEmitido(data) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `CAE ${data.cae || 'OK'}`,
        text: data.numero_completo || '',
        showConfirmButton: false,
        timer: 3200,
      });
      await this.cargarComprobantesArca();
    },
    async reembolsar(p) {
      if (!this.appOnline) {
        await Swal.fire({
          icon: 'info',
          title: 'Sin conexión',
          text: 'Para reembolsar necesitás internet: se restaura el stock y se mueve la caja en el servidor.',
        });
        return;
      }
      const fac = this.facturaDe(p);
      const html = [
        `<p class="mb-2 text-start">Se va a anular el cobro de <strong>$${this.money(p.total)}</strong>.</p>`,
        '<ul class="text-start small mb-0 ps-3">',
        '<li>El stock de los productos vuelve al depósito.</li>',
        '<li>El dinero se descuenta de la caja abierta.</li>',
        fac ? '<li>Podés emitir la nota de crédito ARCA en el mismo paso.</li>' : '',
        '</ul>',
      ].join('');
      const dialog = {
        icon: 'warning',
        title: '¿Reembolsar esta venta?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Reembolsar',
        cancelButtonText: 'Volver',
        confirmButtonColor: '#dc2626',
        focusCancel: true,
      };
      if (fac && this.puedeFacturarArca && !this.ncDe(p)) {
        dialog.input = 'checkbox';
        dialog.inputValue = 1;
        dialog.inputPlaceholder = 'Emitir nota de crédito ARCA';
      }
      const ask = await Swal.fire(dialog);
      if (!ask.isConfirmed) return;

      const emitirNc =
        fac && this.puedeFacturarArca && !this.ncDe(p) ? ask.value === 1 || ask.value === true : false;
      this.reembolsando = p.pedido_id;
      try {
        const usuario = localStorage.getItem('usuario');
        const { data } = await axios.post(`/pedidos/${p.pedido_id}/reembolso`, {
          usuario,
          emitir_nc: emitirNc,
        });
        if (data.nc_error) {
          await Swal.fire({
            icon: 'warning',
            title: 'Reembolso hecho',
            text: `La venta se reembolsó, pero la nota de crédito no se emitió: ${data.nc_error}`,
          });
        } else {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: data.nc ? 'Reembolso y nota de crédito OK' : 'Reembolso registrado',
            showConfirmButton: false,
            timer: 2800,
          });
        }
        await this.cargar();
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'No se pudo reembolsar.';
        await Swal.fire({ icon: 'error', title: 'No se reembolsó', text: msg });
      } finally {
        this.reembolsando = null;
      }
    },
    setupLiveUpdates() {
      let wasOnline = null;
      this.offConnectivity = onConnectivityChange((online) => {
        if (wasOnline === null) {
          wasOnline = online;
          return;
        }
        if (online && !wasOnline) void this.cargar();
        wasOnline = online;
      });
      this.offSyncEvents = subscribePosSyncEvents(() => {
        void this.cargar();
      });
    },
    etiquetaSync(p) {
      if (!p.offline) return '';
      if (p.ticket_folio) return p.ticket_folio;
      const map = {
        pending: 'Pendiente sync',
        syncing: 'Sincronizando',
        failed: 'Error sync',
        auth_required: 'Re-login',
        caja_mismatch: 'Caja distinta',
        upgrade_required: 'Actualizar POS',
        synced_with_warnings: 'Con avisos',
      };
      return map[p.sync_status] || 'Offline';
    },
    esAdmin() {
      return isAdmin();
    },
    esVentaPos,
    money(v) {
      return Number(v || 0).toFixed(2);
    },
    toggleExpand(p) {
      this.expandido = this.expandido === p.pedido_id ? null : p.pedido_id;
    },
    aplicarPedidoDesdeQuery() {
      const raw = this.$route.query.pedido;
      const id = raw != null && raw !== '' ? Number(raw) : NaN;
      if (!Number.isFinite(id) || id <= 0) return;
      const p = this.cobrosMostrador.find((x) => x.pedido_id === id);
      if (p && this.rango === 'hoy' && !this.esHoy(p.creado_en)) this.rango = 'todos';
      if (p && this.esCancelado(p)) this.filtroEstado = 'canceladas';
      this.expandido = id;
      this.$nextTick(() => {
        const el = document.getElementById(`pedido-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    itemsDe(p) {
      try {
        const raw = p.items_json;
        if (typeof raw === 'string') return JSON.parse(raw);
        return Array.isArray(raw) ? raw : [];
      } catch {
        return [];
      }
    },
    esHoy(iso) {
      if (!iso) return false;
      try {
        const d = new Date(iso);
        const n = new Date();
        return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
      } catch {
        return false;
      }
    },
    formatearFecha(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
      } catch {
        return String(iso);
      }
    },
    async cargar() {
      this.cargando = true;
      this.avisoTabla = '';
      try {
        const usuario = localStorage.getItem('usuario');
        this.cobros = await resolveCobrosList(usuario);
        if (this.expandido != null && !this.cobros.some((x) => x.pedido_id === this.expandido)) {
          this.expandido = null;
        }
        this.$nextTick(() => this.aplicarPedidoDesdeQuery());
        if (this.puedeFacturarArca) await this.cargarComprobantesArca();
      } catch {
        this.avisoTabla = 'No se pudieron cargar los cobros.';
        this.cobros = [];
      } finally {
        this.cargando = false;
      }
    },
    exportar() {
      const rows = [];
      for (const p of this.cobrosFiltrados) {
        const items = this.itemsDe(p);
        const fac = this.facturaDe(p);
        const base = {
          Fecha: this.formatearFecha(p.creado_en),
          Estado: this.esCancelado(p) ? 'Reembolsada' : 'Cobrada',
          Vendedor: p.vendedor || '',
          Cliente: p.cliente_nombre || '',
          Medio: p.medio_pago || '',
          Total: Number(p.total) || 0,
          ARCA: fac ? `${fac.cbte_tipo_label || ''} ${fac.numero_completo || ''}`.trim() : '',
        };
        if (!items.length) {
          rows.push({ ...base, Producto: '', Cantidad: '', Precio: '' });
        } else {
          for (const it of items) {
            rows.push({
              ...base,
              Producto: it.producto_nombre || '',
              Cantidad: it.cantidadSeleccionada != null ? it.cantidadSeleccionada : '',
              Precio: this.precioUnitario(it),
            });
          }
        }
      }
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ventas POS');
      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const u = localStorage.getItem('usuario') || 'negocio';
      saveAs(new Blob([out], { type: 'application/octet-stream' }), `ventas_pos_${u}.xlsx`);
    },
  },
};
</script>

<style scoped>
.cobros-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.cobros-toolbar__search {
  position: relative;
  flex: 1 1 180px;
  min-width: 160px;
  max-width: 340px;
}

.cobros-toolbar__search i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.85rem;
}

.cobros-toolbar__search input {
  padding-left: 2rem;
  border-radius: 999px;
}

.cobro-stat {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  height: 100%;
}

.cobro-stat--accent {
  background: linear-gradient(135deg, #16a34a 0%, #0d9488 100%);
  border-color: #16a34a;
  color: #fff;
}

.cobro-stat__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 600;
}

.cobro-stat--accent .cobro-stat__label {
  color: rgba(255, 255, 255, 0.85);
}

.cobro-stat__num {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.1;
}

.cobro-card {
  border-radius: 16px;
  overflow: hidden;
}

.cobro-card--cancel {
  opacity: 0.9;
}

.cobro-card__hit {
  border: none;
  background: transparent;
}

.cobro-card__hit:hover {
  background: rgba(15, 23, 42, 0.025);
}

.cobro-card__top,
.cobro-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem 0;
}

.cobro-card__meta {
  padding: 0.45rem 1rem 0.9rem;
}

.cobro-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  min-width: 0;
}

.cobro-card__amount {
  font-weight: 800;
  font-size: 1.05rem;
  white-space: nowrap;
}

.cobro-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 650;
  border: 1px solid transparent;
}

.cobro-chip--pay {
  background: #dcfce7;
  color: #166534;
  border-color: #bbf7d0;
}

.cobro-chip--ok {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.cobro-chip--nc {
  background: #ede9fe;
  color: #6d28d9;
  border-color: #ddd6fe;
}

.cobro-chip--wait {
  background: #fff7ed;
  color: #c2410c;
  border-color: #fed7aa;
}

.cobro-chip--cancel {
  background: #fee2e2;
  color: #b91c1c;
  border-color: #fecaca;
}

.cobro-chip--off {
  background: #f1f5f9;
  color: #475569;
}

.cobro-card__chev {
  color: #64748b;
  transition: transform 0.2s ease;
}

.cobro-card__chev--up {
  transform: rotate(180deg);
}

.cobro-card__detail {
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 0.9rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.cobro-card__h {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #64748b;
  margin: 0 0 0.4rem;
}

.cobro-items {
  max-height: 220px;
  overflow-y: auto;
}

.cobro-items__row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #eef2f7;
  font-size: 0.9rem;
}

.cobro-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

@media (max-width: 575.98px) {
  .cobro-card__actions .btn {
    flex: 1 1 calc(50% - 0.45rem);
    min-height: 42px;
  }

  .cobros-toolbar .btn-group {
    width: 100%;
  }

  .cobros-toolbar .btn-group .btn {
    flex: 1;
  }

  .cobros-toolbar__search {
    max-width: none;
    flex-basis: 100%;
  }
}

.cobro-card__amount {
  font-weight: 800;
  font-size: 1.05rem;
  white-space: nowrap;
}

.cobro-stat,
.cobro-stat {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.85rem 1rem;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  height: 100%;
}

.cobro-stat--accent,
.cobro-stat--accent {
  background: linear-gradient(135deg, #16a34a 0%, #0d9488 100%);
  border-color: #16a34a;
  color: #fff;
}
</style>
